import Vue, { h, Ref } from 'vue';
import { IssueLegacy } from '@/ui/pages/checklist/adaptors/IssueAdaptors';
import { inputField } from '@/ui/components/form';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions } from '@/ui/types';
import { Field } from '@/ui/components/form/Field';
import PrioritySelector from '@/_legacy/components/Issues/PrioritySelector.vue';
import { dueAfterSelection } from '@/ui/components/issueTemplate/issueCreateTemplateModal/DueAfterSelection';
import { DueAfter } from '@/entities/issueTemplate';
import { Task, useValidation } from '@/ui/concerns';
import { buildValidatorInspectionIssue } from '@/entities/issue';
import AddPhotos from '@/_legacy/components/Issues/AddPhotos.vue';
import { LegacyPhoto } from '@/ui/pages/issue/legacyTypes';
import AssigneesDropdown from '@/ui/pages/user/assignees/AssigneesDropdown.vue';
import isEmail from 'validator/lib/isEmail';

export function useFillDetailModal(record: Ref<IssueLegacy>) {
  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(buildValidatorInspectionIssue, record);

  const fields = <T = string>(assigneeOptions: SelectOptions<T>, fetchUsersTask: Task) => [
    record.value.settings.title_enabled
      ? inputField(
        {
          name: 'title',
          label: t('common.title'),
          placeholder: t('issues.form.enter-title'),
          value: record.value.params.title,
          required: record.value.settings.title_required,
          errors: validationErrors.value,
          disabled: false,
        },

        value => {
          record.value.params.title = value;
        },
      )
      : null,

    record.value.settings.description_enabled
      ? inputField(
        {
          type: 'textarea',
          name: 'description',
          label: t('common.description'),
          placeholder: t('issues.form.enter-description'),
          value: record.value.params.description ?? '',
          required: record.value.settings.description_required,
          errors: validationErrors.value,
          disabled: false,
        },

        value => {
          record.value.params.description = value;
        },
      )
      : null,

    record.value.settings.photos_enabled
      ? [
        h('div', { class: 'label' }, [
          t('common.photos'),
          record.value.settings.photos_required ? h('span', { class: 'has-text-danger' }, '*') : null,
        ]),
        h(Field, {
          props: {
            name: 'photos',
            label: 'Photos',
            required: false,
            errors: validationErrors.value,
            hideLabel: true,
          },
        }, [h(AddPhotos, {
          props: {
            value: record.value.photos ?? [],
          },
          on: {
            input: (value: Array<LegacyPhoto>) => {
              Vue.set(record.value, 'photos', value);
            },
          },
        })])] : null,

    record.value.settings.assignees_enabled
      ? [
        h('div', { class: 'label' }, [
          t('issue-template.assignees'),
          record.value.settings.assignees_required ? h('span', { class: 'has-text-danger' }, '*') : null,
        ]),
        h(AssigneesDropdown, {
          props: {
            value: [
              ...(record.value.params.assignees.users || []),
              ...(record.value.params.assignees.groups || []),
              ...(record.value.params.associateAssignees || []),
            ],
            options: assigneeOptions,
            errors: validationErrors.value,
            disabled: false,
            required: record.value.settings.assignees_required,
            placeholder: t('common.select-user-or-group'),
          },
          on: {
            input: (value: Array<string>) => {
              const assignees: Array<string> = [];
              const assignedGroups: Array<string> = [];
              const associateAssignees: Array<string> = [];

              value.forEach(v => {
                if (isEmail(v)) {
                  associateAssignees.push(v);
                } else if (v.includes('user_')) {
                  assignees.push(v);
                } else if (v.includes('group_')) {
                  assignedGroups.push(v);
                }
              });
              record.value.params.assignees.users = assignees;
              record.value.params.assignees.groups = assignedGroups;
              record.value.params.associateAssignees = associateAssignees;
            },
            'user-created': (value: string|number) => {
              record.value.params.assignees.users = [...record.value.params.assignees.users, `user_${value}`];
              fetchUsersTask.run();
            },
          },
        })]
      : null,

    record.value.settings.priority_enabled
      ? [
        h('div', { class: 'label mt-4' }, [
          t('common.priority'),
          record.value.settings.priority_required ? h('span', { class: 'has-text-danger' }, '*') : null,
        ]),
        h(Field, {
          props: {
            name: 'priority',
            label: t('common.priority'),
            required: record.value.settings.priority_required,
            errors: [],
            hideLabel: true,
          },
        }, [h(PrioritySelector, {
          props: {
            value: record.value.params.priority,
          },
          on: {
            input: (value: number) => {
              record.value.params.priority = value;
            },
          },
        })]),
      ] : null,

    record.value.settings.due_after_enabled
      ? [
        h('div', { class: 'mt-5' }, [
          h('div', { class: 'label' }, [
            t('issue-template.due-after-label'),
            record.value.settings.due_after_required ? h('span', { class: 'has-text-danger' }, '*') : null,
          ]),
          dueAfterSelection({
            props: {
              value: record.value.params.due_in,
            },
            on: {
              input: (value: DueAfter) => {
                record.value.params.due_in = value;
              },
            },
          }),
        ]),
      ] : null,
  ];

  return {
    fields,
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  };
}
